import {
  Blocks,
  ChartColumnIncreasing,
  ChartNoAxesCombined,
  CheckCheck,
  Hammer,
  Link,
  Server,
  Settings,
  SlidersHorizontal,
  Spline,
  SquareActivity,
  TvMinimalPlay,
  UserSearch,
  UsersRound,
} from "lucide-react";

import { SparklesSearch } from "@/components/icons/svg/figma/SparklesSearch";

import { RolePermissions } from "@/access-control/types";
import { CanViewDestinationRanks } from "@/admin/useCanViewDestinationRanks";
import { allowAuditSelfServe } from "@/AuditLogs";
import { GetAccessReponse } from "@/entitlements/types";
import FeatureFlag from "@/FeatureFlag";
import { AccountMetadata } from "@/reducers/environments";
import { UsersState } from "@/reducers/users";
import { wtmURLs as WtmUrlsType } from "@/trackers/useWTMURLs";
import { isAccountInHIPAAMode, userIsFPEmployee } from "@/util";
import { NavItemType, RenderNavItemsPropsItem } from "./types";

// System Status Navigation Item
export const getSystemStatusNavItems = (hasAdminAccess: boolean) => [
  {
    title: "System Status",
    href: "/system-status",
    icon: SquareActivity,
    shouldShowFn: () => hasAdminAccess,
  },
];

export const getFreshpaintAINavItems = () => [
  {
    title: "Freshpaint AI",
    href: "/ai-chat",
    icon: SparklesSearch,
  },
];

// Setup Navigation Items
export const getSetupNavItems = () => [
  {
    title: "Setup",
    href: "/sources",
    icon: SlidersHorizontal,
  },
];

// Event Navigation Items
export const getEventNavItems = (
  accountMetadata: AccountMetadata,
  canEditEvents: boolean,
  hasGlobalAllowListEntries: boolean,
) => [
  {
    title: "Events",
    href: "/events",
    icon: Spline,
    items: [
      {
        title: "Library",
        href: "/events/library",
        shouldShowFn: () => canEditEvents,
        activeHrefPrefixes: ["/events/library/new"],
      },
      {
        title: "Live View",
        href: "/events/liveview",
      },
      {
        title: "Event Verification",
        href: "/event-verification",
      },
      {
        title: "Visual Tagger",
        href: "/events/editor",
        shouldShowFn: () => canEditEvents,
      },
      {
        title: "Transformations",
        href: "/transformations",
        activeHrefPrefixes: ["/transformations/new"],
      },
      {
        title: "HIPAA Allow List",
        href: "/hipaa/allowlist",
        shouldShowFn: () =>
          accountMetadata &&
          hasGlobalAllowListEntries &&
          isAccountInHIPAAMode(accountMetadata),
      },
      {
        title: "SQL Editor",
        href: "/sql-editor",
        shouldShowFn: () => !!accountMetadata.config.sqlTransformations,
      },
    ],
  },
];

// Integration Navigation Items V1
export const getIntegrationNavItemsV1 = (): NavItemType[] => [
  {
    title: "Integrations",
    href: "/destinations",
    icon: Blocks,
    items: [
      {
        title: "Apps",
        href: "/destinations/apps",
      },
      {
        title: "Warehouses",
        href: "/destinations/warehouses",
      },
      {
        title: "Consent Management",
        href: "/consent-management",
      },
    ],
  },
];

// Integration Navigation Items V2
export const getIntegrationNavItemsV2 = (
  accountMetadata: AccountMetadata,
  flag: (featureName: FeatureFlag) => boolean,
  canEditDestinations: boolean,
  canAccessAudiences: boolean,
  _accountEntitlements: GetAccessReponse | undefined,
): NavItemType[] => [
  {
    title: "Integrations",
    href: "/destinations",
    icon: Blocks,
    // Prevent parent from being active when children are active
    exact: true,
    items: [
      {
        title: "Overview",
        href: "/destinations/overview",
        activeHrefPrefixes: ["/destinations/overview"],
      },
      {
        title: "Search & Social",
        href: "/destinations/direct-response-ads",
        activeHrefPrefixes: [
          "/destinations/direct-response-ads",
          "/destinations/apps",
        ],
      },
      {
        title: "Display",
        href: "/destinations/demand-side-platforms",
        activeHrefPrefixes: [
          "/destinations/demand-side-platforms",
          "/destinations/apps",
        ],
      },
      {
        title: "Audio",
        href: "/destinations/audio",
        activeHrefPrefixes: ["/destinations/audio", "/destinations/apps"],
      },
      {
        title: "Analytics & Engagement",
        href: "/destinations/data-activation",
        activeHrefPrefixes: [
          "/destinations/data-activation",
          "/destinations/apps",
        ],
      },
      {
        title: "Audience Targeting",
        href: "/destinations/audiences",
        activeHrefPrefixes: ["/destinations/audiences", "/destinations/apps"],
        shouldShowFn: () => flag("audiences") && canAccessAudiences,
      },
      {
        title: "Data Warehouses",
        href: "/destinations/warehouses",
        activeHrefPrefixes: ["/destinations/warehouses", "/destinations/apps"],
      },
      {
        title: "CTV",
        href: "/destinations/ctv",
        activeHrefPrefixes: ["/destinations/ctv", "/destinations/apps"],
      },
      {
        title: "Mobile Measurement",
        href: "/destinations/mmp",
        activeHrefPrefixes: ["/destinations/mmp", "/destinations/apps"],
      },

      {
        title: "Functions",
        href: "/destinations/functions",
        activeHrefPrefixes: ["/destinations/functions", "/destinations/apps"],
        shouldShowFn: () =>
          !!accountMetadata.config.functions && canEditDestinations,
      },
      {
        title: "Catalog",
        href: "/destinations/catalog",
        activeHrefPrefixes: ["/destinations/catalog"],
        shouldShowFn: () => flag("destinationsV2"),
      },
    ],
  },
];

// Analytics Navigation Items
export const getAnalyticsNavItems = (
  flag: (featureName: FeatureFlag) => boolean,
) => [
  {
    title: "Analytics",
    href: "/analytics/dashboard",
    icon: ChartColumnIncreasing,
    shouldShowFn: () => flag("analytics"),
    items: [
      {
        title: "Dashboard",
        href: "/analytics/dashboard",
        shouldShowFn: () => flag("analytics"),
      },
      {
        title: "Campaigns",
        href: "/analytics/campaigns",
        shouldShowFn: () => flag("analytics"),
      },
      {
        title: "Service Lines",
        href: "/analytics/service-lines",
        shouldShowFn: () => flag("analytics"),
      },
    ],
  },
];

// Insights Navigation Items
export const getInsightsNavItems = (
  flag: (featureName: FeatureFlag) => boolean,
  canAccessOfflineAttributions: boolean,
) => [
  {
    title: "Insights",
    href: "/insights",
    icon: ChartNoAxesCombined,
    flag: "new",
    items: [
      {
        title: "Overview",
        href: "/insights",
        exact: true,
        tracking: "data-insights-navigation",
      },
      {
        title: "Ad Performance",
        href: "/marketing-performance-analysis",
        activeHrefPrefixes: ["/marketing-performance-analysis"],
        shouldShowFn: () =>
          canAccessOfflineAttributions && flag("marketingPerformanceAnalysis"),
      },
      {
        title: "Web Analytics",
        href: "/web-analytics",
      },
      {
        title: "Funnels",
        href: "/funnels",
        activeHrefPrefixes: ["/funnels"],
      },
      {
        title: "Heatmaps",
        href: "/heatmaps",
      },
      {
        title: "Session Replay",
        href: "/session-replay",
      },
      {
        title: "Surveys",
        href: "/surveys",
        activeHrefPrefixes: ["/surveys"],
      },
      {
        title: "Money Saved Report",
        href: "/money-saved",
        shouldShowFn: () => flag("savedMoneyDashboard") && userIsFPEmployee(),
      },
    ],
  },
];

// Audience Navigation Items
export const getAudienceNavItems = (
  flag: (featureName: FeatureFlag) => boolean,
  canAccessAudiences: boolean,
) => [
  {
    title: "Audiences",
    href: "/audiences",
    icon: UsersRound,
    shouldShowFn: () => canAccessAudiences && flag("audiences"),
    items: [
      {
        title: "Overview",
        href: "/audiences/overview",
      },
      {
        title: "Imported Audiences",
        href: "/audiences/imported",
        activeHrefPrefixes: ["/audiences/imported"],
      },
      {
        title: "Segments",
        href: "/audiences/segments",
        activeHrefPrefixes: ["/audiences/segments"],
        flag: !flag("audiences") ? "coming soon" : undefined,
      },
    ],
  },
];

// Profiles Navigation Items
export const getProfilesNavItems = (
  flag: (featureName: FeatureFlag) => boolean,
  canAccessUserProfiles: boolean,
) => [
  {
    title: "Profiles",
    href: "/profiles",
    icon: UserSearch,
    flag: "new",
    shouldShowFn: () =>
      userIsFPEmployee() && canAccessUserProfiles && flag("userProfiles"),
    activeHrefPrefixes: ["/profiles"],
  },
];

export const getConsentManagementNavItems = (
  flag: (featureName: FeatureFlag) => boolean,
) => {
  if (!flag("consentMetrics")) {
    return [
      {
        title: "Consent",
        icon: CheckCheck,
        flag: "new",
        href: "/consent-management/overview",
        activeHrefPrefixes: ["/consent-management"],
      },
    ];
  }

  return [
    {
      title: "Consent",
      icon: CheckCheck,
      flag: "new",
      href: "/consent-management/overview",
      activeHrefPrefixes: ["/consent-management"],
      items: [
        {
          title: "Overview",
          href: "/consent-management/overview",
          activeHrefPrefixes: ["/consent-management/overview"],
        },
        {
          title: "Metrics",
          href: "/consent-management/metrics",
          activeHrefPrefixes: ["/consent-management/metrics"],
          shouldShowFn: () => flag("consentMetrics"),
        },
      ],
    },
  ];
};

// Short Links Navigation Items
export const getShortLinksNavItems = (
  flag: (featureName: FeatureFlag) => boolean,
) => {
  if (!flag("shortLinks")) {
    return [];
  }
  return [
    {
      title: "Short Links",
      icon: Link,
      flag: "new",
      href: "/short-links/overview",
      activeHrefPrefixes: ["/short-links"],
      items: [
        {
          title: "Overview",
          href: "/short-links/overview",
        },
        {
          title: "Links",
          href: "/short-links",
          exact: true,
        },
        {
          title: "Tags",
          href: "/short-links/tags",
        },
        {
          title: "Domains",
          href: "/short-links/domains",
          flag: "coming soon",
        },
      ],
    },
  ];
};

// Video Navigation Items
export const getVideoNavItems = (
  accountEntitlements: GetAccessReponse | undefined,
  canAccessVideo: boolean,
) => {
  const items: any[] = [];

  const isVideoEnabled = accountEntitlements?.account_access.some(
    (access: any) => access.entitlement_key === "video",
  );

  if (isVideoEnabled && canAccessVideo) {
    items.push({
      title: "Video",
      href: "/video",
      openNewTab: false,
      icon: TvMinimalPlay,
    });
  }

  return items;
};

export const getWebTrackerMonitoringNavItems = (
  wtmURLs: WtmUrlsType,
  flag: (featureName: FeatureFlag) => boolean,
  accountMetadata: AccountMetadata,
  canAccessWtm: boolean,
) =>
  canAccessWtm && accountMetadata.config.webTrackerMonitoring
    ? [
        {
          title: "Web Trackers",
          href: wtmURLs.trackersOverview,
          icon: SquareActivity,
          shouldShowFn: () => flag("webTrackerMonitoring"),
        },
      ]
    : [];

// Internal Tools Navigation Items
export const getInternalToolsNavItems = (
  canViewDestinationRanks: CanViewDestinationRanks,
  flag: (featureName: FeatureFlag) => boolean,
) => [
  {
    title: "Internal Tools",
    href: "/admin",
    icon: Hammer,
    shouldShowFn: userIsFPEmployee,
    items: [
      {
        title: "Masquerade",
        href: "/masquerade",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "MTU Usage",
        href: "/admin/mtureports",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Entitlements",
        href: "/admin/entitlements",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "GTM Migration",
        href: "/admin/gtmmigration",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Video Extractor",
        href: "/admin/videoextractor",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Cross Domain Tracking",
        href: "/admin/crossdomaintracking",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Epic Share Link",
        href: "/admin/epicsharelink",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "AdBlock Settings",
        href: "/admin/adblock",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Ad Performance",
        href: "/admin/adperformance",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Integration Ranks",
        href: "/admin/destination-ranks",
        shouldShowFn: () =>
          userIsFPEmployee() && canViewDestinationRanks.canViewDestinationRanks,
      },
      {
        title: "Integration Apps",
        href: "/destinations/apps",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Accounts",
        href: "/admin/accounts",
        shouldShowFn: userIsFPEmployee,
        activeHrefPrefixes: ["/admin/accounts"],
      },
      {
        title: "Prototype Lab",
        href: "/admin/prototypes",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Accessibility",
        href: "/accessibility",
        activeHrefPrefixes: ["/accessibility"],
        shouldShowFn: () =>
          userIsFPEmployee() && flag("accessibilityCompliance"),
      },
      {
        title: "Audience Auditor",
        href: "https://chatgpt.com/g/g-6967dc1643d48191a377156ab9d9be05-audience-auditor",
        shouldShowFn: userIsFPEmployee,
        openNewTab: true,
      },
      {
        title: "AI Visibility",
        href: "/admin/ai-visibility",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Bulk Operations",
        href: "/bulk-operations",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "AI Token Usage",
        href: "/admin/ai-token-usage",
        shouldShowFn: userIsFPEmployee,
      },
    ],
  },
];

// WTM Admin Navigation Items
export const getWTMAdminNavItems = () => [
  {
    title: "WTM Internal",
    href: "/admin",
    icon: Server,
    shouldShowFn: userIsFPEmployee,
    items: [
      {
        title: "Catalog",
        href: "/admin/trackers/catalog",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Manage Recurring Scans",
        href: "/admin/trackers/scanMonitoring",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Onboarded Domains",
        href: "/admin/trackers/domainAdmin",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Set Up Domain",
        href: "/admin/trackers/setUpDomain",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Latest Reports",
        href: "/admin/trackers/reportAdmin",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Search",
        href: "/admin/trackers/reportsByTrackerDomain",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Scan Sites",
        href: "/admin/trackers/siteScanner",
        shouldShowFn: userIsFPEmployee,
      },
    ],
  },
];

// Settings Navigation Items
export const getSettingsNavItems = (
  flag: (featureName: FeatureFlag) => boolean,
  usersState: UsersState,
  rolePermissions: RolePermissions | undefined,
): RenderNavItemsPropsItem[] => [
  {
    title: "Settings",
    href: "/settings",
    icon: Settings,
    items: [
      {
        title: "Team Members",
        href: "/settings/team",
      },
      {
        title: "Projects",
        href: "/settings/projects",
      },
      {
        title: "Audit Logs",
        href: "/audit-logs",
        shouldShowFn: () =>
          allowAuditSelfServe(flag, usersState.currentUser, rolePermissions),
      },
    ],
  },
];
